import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useScheduleCall } from "../context/ScheduleCallContext";
import {
  LayoutTemplate,
  Phone,
  Globe,
  CalendarCheck,
  Users,
  Bot,
  Clock,
  Zap,
  Rocket,
  ChevronRight,
} from "lucide-react";
import TiltCard from "./TiltCard";
import BusinessWebsiteSuiteShowcase from "./BusinessWebsiteSuiteShowcase";

const iconProps = { className: "w-7 h-7", strokeWidth: 1.75 };

const valueProps = [
  {
    title: "Reusable SaaS",
    description: "Battle-tested platforms — not one-off builds. Deploy the same product for every new customer.",
    icon: Rocket,
  },
  {
    title: "Fast Onboarding",
    description: "Branding, config, and go-live in days. Skip months of custom development.",
    icon: Zap,
  },
  {
    title: "Production Ready",
    description: "Already live with real users. Stable, scalable, and maintained by Purpose Labs.",
    icon: Clock,
  },
];

const BWS_SLUG = "business-website-suite";

const products = [
  {
    slug: BWS_SLUG,
    title: "Business Website Suite",
    tagline: "All-in-one growth platform for service businesses",
    description:
      "A complete digital presence for local and service-based businesses — showcase offerings, capture leads, book appointments, and manage customers from one integrated system.",
    features: [
      {
        label: "Professional business website",
        detail: "Showcase services, testimonials, and brand story",
        icon: Globe,
      },
      {
        label: "Appointments & lead generation",
        detail: "Booking flows, forms, and conversion-optimized funnels",
        icon: CalendarCheck,
      },
      {
        label: "Built-in CRM",
        detail: "Track leads, customers, and follow-ups in one dashboard",
        icon: Users,
      },
    ],
    icon: <LayoutTemplate {...iconProps} />,
    glow: "cyan",
    gradient: "from-accent-cyan/20 to-accent-cyan/5",
    badge: "Website + CRM",
  },
  {
    slug: "ai-inbound-phone-agents",
    title: "AI Inbound Phone Agents",
    tagline: "24/7 AI voice agents that never miss a call",
    description:
      "Intelligent phone agents that answer inbound calls around the clock — qualify leads, answer FAQs, book appointments, and route hot prospects to your team automatically.",
    features: [
      {
        label: "Inbound call handling",
        detail: "Natural voice AI that answers every call instantly",
        icon: Phone,
      },
      {
        label: "Lead qualification & booking",
        detail: "Capture intent, schedule appointments, sync to CRM",
        icon: CalendarCheck,
      },
      {
        label: "Multi-tenant SaaS",
        detail: "One platform, many customers — onboard new clients fast",
        icon: Bot,
      },
    ],
    icon: <Phone {...iconProps} />,
    glow: "violet",
    gradient: "from-accent-violet/20 to-accent-violet/5",
    badge: "Voice AI SaaS",
  },
];

const Products = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const { openScheduleCall } = useScheduleCall();
  const [showBwsShowcase, setShowBwsShowcase] = useState(false);

  const handleProductClick = (slug) => {
    if (slug === BWS_SLUG) setShowBwsShowcase(true);
  };

  return (
    <section id="products" className="section-padding bg-dark-400 relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-gradient opacity-40 pointer-events-none" />

      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-14 md:mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-accent-violet font-semibold text-sm uppercase tracking-widest">
            Ready-Made SaaS
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            Products You Can Launch Fast
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Reusable SaaS platforms we&apos;ve already built and battle-tested.
            Onboard your customers in days — not months of custom development.
          </p>
        </motion.div>

        {/* Value props */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 lg:mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.15, duration: 0.6 }}
        >
          {valueProps.map((item) => {
            const ValueIcon = item.icon;
            return (
              <div
                key={item.title}
                className="glass-panel p-5 text-center md:text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-600/20 border border-primary-500/30 flex items-center justify-center text-primary-400 mx-auto md:mx-0 mb-3">
                  <ValueIcon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-white text-sm mb-1">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </motion.div>

        {/* Product cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
            >
              <TiltCard glowColor={product.glow} className="h-full">
                <div
                  role={product.slug === BWS_SLUG ? "button" : undefined}
                  tabIndex={product.slug === BWS_SLUG ? 0 : undefined}
                  onClick={
                    product.slug === BWS_SLUG
                      ? () => handleProductClick(product.slug)
                      : undefined
                  }
                  onKeyDown={
                    product.slug === BWS_SLUG
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleProductClick(product.slug);
                          }
                        }
                      : undefined
                  }
                  className={`glass-panel-hover h-full p-7 md:p-8 bg-gradient-to-br ${product.gradient} group ${
                    product.slug === BWS_SLUG
                      ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan/50"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary-400 group-hover:scale-110 transition-transform duration-300">
                      {product.icon}
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 text-primary-300 border border-white/10 whitespace-nowrap">
                      {product.badge}
                    </span>
                  </div>

                  <h3 className="font-display text-2xl font-bold text-white mb-1 group-hover:text-primary-300 transition-colors">
                    {product.title}
                  </h3>
                  <p className="text-sm text-primary-400/90 font-medium mb-4">
                    {product.tagline}
                  </p>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6">
                    {product.description}
                  </p>

                  <ul className="space-y-4">
                    {product.features.map((feature) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <li key={feature.label} className="flex gap-3">
                          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-primary-400 flex-shrink-0">
                            <FeatureIcon className="w-4 h-4" strokeWidth={1.75} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">
                              {feature.label}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {feature.detail}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {product.slug === BWS_SLUG && (
                    <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between gap-2 text-sm text-accent-cyan group-hover:text-accent-cyan/90 transition-colors">
                      <span className="font-medium">Explore features</span>
                      <ChevronRight
                        className="w-4 h-4 transition-transform group-hover:translate-x-1"
                        strokeWidth={1.75}
                        aria-hidden
                      />
                    </div>
                  )}
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-14 glass-panel p-8 md:p-10"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-3">
            Need a White-Label or Custom Deployment?
          </h3>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto text-sm md:text-base">
            Both products are designed for rapid customer onboarding. Tell us
            about your use case and we&apos;ll get you live fast.
          </p>
          <motion.button
            onClick={openScheduleCall}
            className="btn-primary"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Discuss Product Onboarding
          </motion.button>
        </motion.div>
      </div>

      <BusinessWebsiteSuiteShowcase
        isOpen={showBwsShowcase}
        onClose={() => setShowBwsShowcase(false)}
      />
    </section>
  );
};

export default Products;
